import React, { useEffect, useRef, useState } from 'react';
import styles from '../../styles/PantallaDeProgramasAcademicos.v2.module.css';


const defaultForm = {
  code: '', // Clave del programa
  rvoe: '',
  name: '',
  institution: '',
  campus: '',
  periodType: '',
  section: '',
  duration: '',
  credits: '',
  description: '',
  objectives: '',
  profile: '',
  rvoeDate: '',
  startCycle: '',
  periodTypeId: '',
  cycleId: '',
  modalidad: '',
  status: 'ACTIVO',
};

export default function NewProgramModal({
  open, onClose, onSubmit, initialData, readOnly, mode,
  institutions = [], sections: sectionsProp = [], periodTypes: periodTypesProp = [], cycles: cyclesProp = [], campuses: campusesProp = []
}) {
  const [form, setForm] = useState(defaultForm);
  const [sections, setSections] = useState(sectionsProp);
  const [periodTypes, setPeriodTypes] = useState(periodTypesProp);
  const [cycles, setCycles] = useState(cyclesProp);
  const [campuses, setCampuses] = useState(campusesProp);
  const dialogRef = useRef(null);

  useEffect(() => {
    if (open) {
      // focus the first field
      const first = dialogRef.current?.querySelector('input, select, textarea, button');
      first?.focus();
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Prefill when opening for edit/view, or reset when opening for create
  useEffect(() => {
    if (open) {
      if (initialData) {
        const mapped = { ...defaultForm, ...initialData };
        // Normalizar siempre el valor visible del select a las opciones canónicas
        const candidate = mapped.periodType || initialData.periodType || initialData.period || initialData.tipo_periodo || '';
        if (candidate) {
          const pl = String(candidate).trim().toLowerCase();
          mapped.periodType = pl === 'tetramestre' ? 'Tetramestre' : pl === 'semestre' ? 'Semestre' : '';
        }
        setForm(mapped);
      } else {
        // Reset to default form when opening for create
        setForm(defaultForm);
      }
    }
  }, [open, initialData]);

  // Al abrir el modal, inicializar catálogos dependientes con los props
  useEffect(() => {
    if (open) {
      setSections(sectionsProp);
      setPeriodTypes(periodTypesProp);
      setCycles(cyclesProp);
      setCampuses(campusesProp);
    }
  }, [open, sectionsProp, periodTypesProp, cyclesProp, campusesProp]);

  // Cuando cambia la institución, recargar catálogos dependientes si es necesario
  useEffect(() => {
    let mounted = true;
    const run = async () => {
      const instId = form.institution;
      if (!instId) {
        // Si no hay institución seleccionada, conservamos los catálogos
        // que vinieron por props (para que los selects no se vean vacíos).
        return;
      }
      try {
        // Importar solo aquí para evitar dependencias circulares
        const { getSections, getPeriodTypes, getCycles, getCampusOptions } = await import('../../api/catalogs');
        const sec = await getSections(instId);
        if (mounted) setSections(sec);
        const pts = await getPeriodTypes(instId);
        if (mounted) setPeriodTypes(pts);
        const cyc = await getCycles(instId);
        if (mounted) setCycles(cyc);
        const camp = await getCampusOptions(instId);
        if (mounted) setCampuses(camp);
      } catch {}
    };
    if (open) run();
    return () => { mounted = false; };
  }, [form.institution, open]);

  if (!open) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (readOnly) return;
    onSubmit?.(form);
  };

  return (
    <div className={styles.modalBackdrop} role="presentation" onMouseDown={(e) => {
      if (e.target === e.currentTarget) onClose?.();
    }}>
      <div className={styles.modalContainer} role="dialog" aria-modal="true" aria-labelledby="np-title" ref={dialogRef}>
        <div className={styles.modalHeader}>
          <h2 id="np-title" className={styles.modalTitle}>
            {mode === 'edit' ? 'Editar Programa Académico' : 'Nuevo Programa Académico'}
          </h2>
          <button className={styles.modalClose} aria-label="Cerrar" onClick={onClose}>✕</button>
        </div>
        <form className={styles.modalBody} onSubmit={handleSubmit}>
          <div className={styles.modalGrid}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Clave del programa <span style={{color:'red'}}>*</span></label>
              <input className={styles.input} name="code" value={form.code} onChange={handleChange} disabled={readOnly} required />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>RVOE</label>
              <input className={styles.input} name="rvoe" value={form.rvoe} onChange={handleChange} disabled={readOnly} />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Institución</label>
              <select className={styles.select} name="institution" value={form.institution} onChange={handleChange} disabled={readOnly} style={{ color: '#222' }}>
                <option value="">— Selecciona una institución —</option>
                {institutions.map(inst => (
                  <option key={inst.id} value={inst.id}>{inst.name}</option>
                ))}
              </select>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Nombre del Programa Académico <span style={{color:'red'}}>*</span></label>
              <input
                className={styles.input}
                name="name"
                value={form.name}
                onChange={handleChange}
                disabled={readOnly}
                required={!readOnly}
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Tipo de Periodo Académico</label>
              <select className={styles.select} name="periodTypeId" value={form.periodTypeId} onChange={handleChange} disabled={readOnly} style={{ color: '#222' }}>
                <option value="">— Selecciona un tipo de periodo —</option>
                {periodTypes.map(pt => (
                  <option key={pt.id} value={pt.id}>{pt.name}</option>
                ))}
              </select>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Campus</label>
              <select className={styles.select} name="campus" value={form.campus} onChange={handleChange} disabled={readOnly} style={{ color: '#222' }}>
                <option value="">— Asignar campus —</option>
                {campuses.map(camp => (
                  <option key={camp.id} value={camp.id}>{camp.name} ({camp.code})</option>
                ))}
              </select>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Duración en periodos <span style={{color:'red'}}>*</span></label>
              <input
                type="number"
                className={styles.input}
                name="duration"
                value={form.duration}
                onChange={handleChange}
                inputMode="numeric"
                disabled={readOnly}
                required={!readOnly}
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Sección</label>
              <select className={styles.select} name="section" value={form.section} onChange={handleChange} disabled={readOnly} style={{ color: '#222' }}>
                <option value="">— Selecciona una sección —</option>
                {sections.map(sec => (
                  <option key={sec.id} value={sec.id}>{sec.name}</option>
                ))}
              </select>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Descripción del programa</label>
              <textarea className={styles.textarea} rows={2} name="description" value={form.description} onChange={handleChange} disabled={readOnly} />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Créditos <span style={{color:'red'}}>*</span></label>
              <input
                type="number"
                className={styles.input}
                name="credits"
                value={form.credits}
                onChange={handleChange}
                inputMode="numeric"
                disabled={readOnly}
                required={!readOnly}
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Perfil del egresado</label>
              <textarea className={styles.textarea} rows={2} name="profile" value={form.profile} onChange={handleChange} disabled={readOnly} />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Objetivos</label>
              <textarea className={styles.textarea} rows={2} name="objectives" value={form.objectives} onChange={handleChange} disabled={readOnly} />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Fecha de registro (RVOE)</label>
              <input type="date" className={styles.input} name="rvoeDate" value={form.rvoeDate} onChange={handleChange} disabled={readOnly} />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Ciclo Escolar de inicio</label>
              <select className={styles.select} name="cycleId" value={form.cycleId} onChange={handleChange} disabled={readOnly} style={{ color: '#222' }}>
                <option value="">— Selecciona un ciclo escolar —</option>
                {cycles.map(cyc => (
                  <option key={cyc.id} value={cyc.id}>{cyc.name}</option>
                ))}
              </select>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Modalidad</label>
              <select className={styles.select} name="modalidad" value={form.modalidad || ''} onChange={handleChange} disabled={readOnly} style={{ color: '#222' }}>
                <option value="">— Selecciona una modalidad —</option>
                <option value="Escolarizada">Escolarizada</option>
                <option value="No escolarizada">No escolarizada</option>
                <option value="Mixta">Mixta</option>
              </select>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Estado</label>
              <div className={styles.statusSelectWrapper}>
                <span className={`${styles.statusDot} ${form.status === 'ACTIVO' ? styles.dotGreen : styles.dotRed}`} />
                <select
                  className={`${styles.statusSelect} ${form.status === 'ACTIVO' ? styles.activeSelect : styles.inactiveSelect}`}
                  name="status"
                  value={form.status}
                  onChange={handleChange}
                  disabled={readOnly}
                >
                  <option value="ACTIVO">ACTIVO</option>
                  <option value="INACTIVO">INACTIVO</option>
                </select>
              </div>
            </div>
          </div>
          <div className={styles.modalActions}>
            {!readOnly && <button type="submit" className={styles.primaryButton}>GUARDAR</button>}
            <button type="button" className={styles.secondaryButton} onClick={onClose}>{readOnly ? 'CERRAR' : 'CANCELAR'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
